import { useMemo } from "react";
import { Check, User } from "lucide-react";

import { Combobox } from "@/components/ui/combobox";
import type { SelectOptionItem } from "@/lib/models/ui";
import { cn } from "@/lib/utils";

import type { CustomerMinimal, Suggestion } from "../state/types";

type CustomerPickerProps = {
  selected_customer_id: string | null;
  /** Up to ~5 suggestions, already sorted by score desc. */
  suggestions: Suggestion[];
  /** Full customer list, used to populate the combobox. */
  customers: CustomerMinimal[];
  disabled?: boolean;
  /**
   * Optional inline error message rendered under the picker (e.g.
   * "Selected customer is already used by another group").
   */
  errorMessage?: string;
  /**
   * `null` means deselect. Clicking the highlighted suggestion mini-card
   * deselects; choosing the same value in the combobox is a no-op.
   */
  onChange: (customerId: string | null) => void;
};

function formatScorePercent(score: number): string {
  // Clamp into [0, 1] then render as integer percent. Scores beyond
  // [0, 1] should never occur, but we guard anyway so the UI never
  // shows nonsense.
  const clamped = Math.max(0, Math.min(1, score));
  return `${Math.round(clamped * 100)}%`;
}

/**
 * Customer picker shared by Phase 2 and Phase 3 group cards.
 *
 * - Single source of truth: `selected_customer_id` passed in from the
 *   parent. The picker never stores its own selection or
 *   `selection_source` (per spec).
 * - Suggestion mini-cards (above the combobox) and the full combobox
 *   both call `onChange` with the chosen customer id.
 * - "Selected from suggestions" is derived: if the current selection
 *   appears in `suggestions`, that mini-card is highlighted. Otherwise
 *   the suggestion row is dimmed (signalling that the current
 *   selection came from the combobox).
 * - Mini-cards are used instead of chips because customer long_names
 *   can be very long and chips truncate badly.
 */
export function CustomerPicker({
  selected_customer_id,
  suggestions,
  customers,
  disabled,
  errorMessage,
  onChange,
}: CustomerPickerProps) {
  const customerById = useMemo((): Map<string, CustomerMinimal> => {
    const map = new Map<string, CustomerMinimal>();
    for (const c of customers) map.set(c.id, c);
    return map;
  }, [customers]);

  const comboboxOptions = useMemo(
    (): SelectOptionItem<string>[] =>
      customers.map((c: CustomerMinimal) => ({
        value: c.id,
        label: c.long_name,
      })),
    [customers],
  );

  const selectionIsFromSuggestions =
    selected_customer_id !== null &&
    suggestions.some((s: Suggestion) => s.customer_id === selected_customer_id);

  // When something is selected but it's NOT one of the suggestions, the
  // suggestion row gets visually dimmed to signal "your current pick
  // came from the full list".
  const suggestionRowDimmed =
    selected_customer_id !== null && !selectionIsFromSuggestions;

  return (
    <div className="flex flex-col gap-2">
      {suggestions.length > 0 ? (
        <div className="flex flex-col gap-1">
          <span className="text-[10px] uppercase font-semibold tracking-wide text-muted-foreground">
            Suggested customers
          </span>
          <div
            className={cn(
              "flex flex-col gap-1",
              suggestionRowDimmed && "opacity-60",
            )}
          >
            {suggestions.map((s: Suggestion) => {
              const customer = customerById.get(s.customer_id);
              if (!customer) return null;
              const isSelected = s.customer_id === selected_customer_id;
              return (
                <button
                  key={s.customer_id}
                  type="button"
                  disabled={disabled}
                  onClick={() =>
                    onChange(isSelected ? null : s.customer_id)
                  }
                  title={customer.long_name}
                  aria-pressed={isSelected}
                  className={cn(
                    "flex flex-row items-start gap-2 rounded-md border bg-card px-2 py-1 text-left transition-colors",
                    "disabled:opacity-50 disabled:cursor-not-allowed",
                    isSelected
                      ? "border-primary bg-primary/10"
                      : "hover:bg-muted/40",
                  )}
                >
                  {isSelected ? (
                    <Check
                      className="shrink-0 h-3.5 w-3.5 mt-0.5 text-primary"
                      aria-hidden
                    />
                  ) : (
                    <User
                      className="shrink-0 h-3.5 w-3.5 mt-0.5 text-muted-foreground"
                      aria-hidden
                    />
                  )}
                  <div className="flex-1 min-w-0 break-words leading-snug">
                    <div className="text-xs">{customer.long_name}</div>
                    <div className="text-[10px] text-muted-foreground">
                      {formatScorePercent(s.score)} · {s.reason}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      ) : null}

      <div className="flex flex-col gap-1">
        <span className="text-[10px] uppercase font-semibold tracking-wide text-muted-foreground">
          All customers
        </span>
        <Combobox
          options={comboboxOptions}
          value={selected_customer_id ?? ""}
          onValueChange={(v: string) => onChange(v === "" ? null : v)}
          placeholder="Select customer..."
          disabled={disabled}
          virtualizeList
          dwimOnChange
          isModal
          popoverClassName="md:min-w-[300px] lg:min-w-[400px] xl:min-w-[500px]"
        />
      </div>

      {errorMessage ? (
        <div className="rounded border border-destructive bg-destructive/10 p-2 text-xs text-destructive">
          {errorMessage}
        </div>
      ) : null}
    </div>
  );
}
